/**
 * Acheminement des expositions d'experience ("Experiment Viewed") vers Segment.
 *
 * GrowthBook tranche la variante vers 1 s, alors que Segment est en delayLoad et
 * n'est pret que vers 2 s. Toute emission directe au moment de l'evaluation est
 * donc perdue, et le SDK marque malgre tout l'experience comme suivie : il n'y a
 * pas de seconde chance.
 *
 * Deux relais sont donc poses :
 * - tant qu'aucun trackingCallback n'est configure, le SDK empile les expositions
 *   (`getDeferredTrackingCalls`). On pose le callback une fois Segment charge, ce
 *   qui vide la pile d'un coup.
 * - sur un test de redirection, la page est detruite avant Segment. L'exposition
 *   est alors recopiee en sessionStorage et emise depuis la page d'arrivee, qui
 *   n'evalue elle-meme aucune experience.
 */

import isBrowser from "../../utilities/isBrowser"

const PENDING_KEY = "gb_pending_exposures"
const SEGMENT_POLL_MS = 100
const SEGMENT_MAX_ATTEMPTS = 100

/**
 * @param {Object} exposure
 * @param {string} exposure.experiment_id
 * @param {string} exposure.variation_id
 * @returns {void}
 */
const trackExposure = exposure => {
  window.analytics.track("Experiment Viewed", exposure)
}

/**
 * @returns {Array<Object>}
 */
const readPendingExposures = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(PENDING_KEY)) || []
  } catch (error) {
    return []
  }
}

/**
 * @param {Array<Object>} exposures
 * @returns {void}
 */
const writePendingExposures = exposures => {
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(exposures))
  } catch (error) {
    // sessionStorage indisponible (navigation privee, cookies bloques) :
    // l'exposition est abandonnee plutot que de casser la redirection.
  }
}

/**
 * Recopie les expositions en attente dans le SDK vers sessionStorage, pour les
 * emettre depuis la page suivante. A appeler avant toute navigation forcee.
 *
 * Si Segment etait deja pret, la pile du SDK a ete videe a la pose du callback
 * et il n'y a rien a reporter.
 *
 * @param {Object} growthbook
 * @returns {void}
 */
export const persistDeferredExposures = growthbook => {
  if (!isBrowser() || !growthbook) return

  const deferred = growthbook.getDeferredTrackingCalls()
  if (!deferred.length) return

  writePendingExposures([
    ...readPendingExposures(),
    ...deferred.map(({ experiment, result }) => ({
      experiment_id: experiment.key,
      variation_id: result.key
    }))
  ])

  // L'exposition appartient desormais a la page d'arrivee. Sans ce vidage, elle
  // repartirait aussi d'ici si Segment finissait de charger avant la navigation.
  growthbook.setDeferredTrackingCalls([])
}

/**
 * @param {Function} callback
 * @returns {void}
 */
const whenSegmentReady = callback => {
  let attempts = 0

  const check = () => {
    if (window.analytics && window.analytics.track) {
      callback()
      return
    }

    attempts += 1
    if (attempts < SEGMENT_MAX_ATTEMPTS) {
      setTimeout(check, SEGMENT_POLL_MS)
    }
  }

  check()
}

/**
 * Branche GrowthBook sur Segment des que celui-ci est charge : emet les
 * expositions reportees depuis une page precedente, puis celles empilees par le
 * SDK sur la page courante (`setTrackingCallback` les rejoue).
 *
 * @param {Object} growthbook
 * @returns {void}
 */
export const startExperimentTracking = growthbook => {
  if (!isBrowser() || !growthbook) return

  whenSegmentReady(() => {
    const pending = readPendingExposures()
    if (pending.length) {
      pending.forEach(trackExposure)
      writePendingExposures([])
    }

    growthbook.setTrackingCallback((experiment, result) => {
      trackExposure({
        experiment_id: experiment.key,
        variation_id: result.key
      })
    })
  })
}
